import useCreateEstimate from '@/api/mutations/estimates/useCreateEstimate';
import useGetProjectEstimates from '@/api/queries/project/useGetProjectEstimates';
import { GetProjectHeatLossEstimateQuery } from '@/gql/graphql';
import { AnchorButton, Badge, Button, Card, H3 } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { GridElement } from '../GridElement';
import HeatLossAssumptionsAlert from '../HeatLossAssumptionsAlert';
import { BrandedCardTitle } from './BrandedCardTitle';
import SuggestedEquipment from './SuggestedEquipment';

type HeatLossEstimate = NonNullable<
  GetProjectHeatLossEstimateQuery['projectById']['heatLossEstimate']
>;

type FlowHeatingCardProps = {
  heatLossEstimate: HeatLossEstimate;
};

export const FlowCardTitle: FC = () => (
  <span className="flex min-w-0 items-center gap-3">
    <span
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-50 text-cyan-600"
    >
      <UntitledIcon name="thermometer-02.3" className="h-5 w-5" />
    </span>
    <span>
      <BrandedCardTitle brand="Flow" descriptor="Heating & cooling" />
    </span>
  </span>
);

const FlowEstimatesAction: FC<{ projectId: string }> = (props) => {
  const { projectId } = props;
  const { project } = useGetProjectEstimates(projectId);
  const history = useHistory();
  const { mutate: createEstimate, isPending } = useCreateEstimate({
    onSuccess: (data) => {
      history.push(
        `/projects/${projectId}/estimates/${data.createEstimate.id}`
      );
    },
  });
  const estimatesCount = project?.estimates.filter((estimate) =>
    estimate.categories.includes('HEAT_PUMP')
  ).length;

  if (estimatesCount == null) return null;

  if (estimatesCount === 0) {
    return (
      <Button
        variant="secondary"
        size="sm"
        isProcessing={isPending}
        disabled={isPending}
        onClick={() => createEstimate({ projectId, categories: ['HEAT_PUMP'] })}
      >
        Create heat pump estimate
      </Button>
    );
  }

  return (
    <AnchorButton
      as={Link}
      to={`/projects/${projectId}/estimates`}
      variant="secondary"
      size="sm"
    >
      View estimates{' '}
      <Badge variant="white" size="sm" rounded>
        {estimatesCount}
      </Badge>
    </AnchorButton>
  );
};

/**
 * Design tab card for Flow, the heating & cooling design product — the
 * property's heat loss estimate and heat pump sizing today; the cooling side
 * arrives with US datasets.
 */
const FlowHeatingCard: FC<FlowHeatingCardProps> = (props) => {
  const { heatLossEstimate } = props;
  const { projectId } = useParams<{ projectId: string }>();

  const {
    heatPumpSizeRange,
    minimumCylinderLitres,
    peakHeatLoad,
    method,
    targetIndoorTemperatureC,
  } = heatLossEstimate;

  return (
    <Card>
      <Card.Body className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <H3>
              <FlowCardTitle />
            </H3>
          </div>
          {projectId && (
            <div className="shrink-0">
              <FlowEstimatesAction projectId={projectId} />
            </div>
          )}
        </div>
        {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-4">
          <GridElement title="Peak heat loss">
            {peakHeatLoad.toFixed(2)}kW
          </GridElement>
          <GridElement title="Heat pump size">
            {heatPumpSizeRange.lowerKw}–{heatPumpSizeRange.upperKw}kW
          </GridElement>
          {minimumCylinderLitres != null && (
            <GridElement title="Minimum cylinder size">
              {minimumCylinderLitres}L
            </GridElement>
          )}
        </div>
        <HeatLossAssumptionsAlert
          method={method}
          targetIndoorTemperatureC={targetIndoorTemperatureC}
        />
        <SuggestedEquipment
          items={[
            {
              label: 'Heat pump',
              name:
                heatLossEstimate.recommendedHeatPumpAppliances[0]?.appliance
                  .productSpec.name ?? '',
            },
            {
              label: 'Cylinder',
              name:
                heatLossEstimate.recommendedCylinderAppliances[0]?.appliance
                  .productSpec.name ?? '',
            },
          ]}
        />
      </Card.Body>
    </Card>
  );
};

export default FlowHeatingCard;
